import { Suspense, lazy, useEffect, type ReactElement } from 'react'
import { Provider } from 'react-redux'
import { Admin, localStorageStore, Resource, CustomRoutes } from 'react-admin'
import { PersistGate } from 'redux-persist/lib/integration/react'
import polyglotI18nProvider from 'ra-i18n-polyglot'
import { ThemeProvider } from '@mui/styles'
import { createBrowserHistory } from 'history'
import { StyledEngineProvider } from '@mui/material/styles'
import createAdminStore from './store'
import { QueryClientProvider } from 'react-query'
import { queryClient } from './common/queryClient'
import authProvider from './authProvider'

import { AppContextProvider } from './context/AppContext'

import { Login, Layout } from './layout'
import { Dashboard } from './dashboard'

import customRoutes, { lazyRouter, resourceRouter } from './routes'
import ProtectedRoute from '@component/Common/ProtectedRoute'
import type { FeatureName } from '@hooks/useFeature'
import englishMessages from './i18n/en'

const MarketingList = lazy(() => import('./analytics/marketing/MarketingList'))
const TenantExperienceEvent = lazy(() => import('./analytics/tenant/Event'))
const OperationsList = lazy(() => import('./analytics/operations'))
const PropertyList = lazy(() => import('./analytics/property'))
const SubscriptionList = lazy(() => import('@pages/settings/SubscriptionList'))
const AccountPropertyList = lazy(() => import('./account/PropertyList'))
const PropertyAdd = lazy(() => import('./account/PropertyAdd'))
const ImportQRCode = lazy(() => import('./qrcodes/ImportQRCode'))
import { theme } from './themes'
import dataProvider from './dataProvider/dataProvider'
import { LocalizationProvider } from '@mui/x-date-pickers'
const QRSupportReport = lazy(() => import('@analytics/marketing/QRSupportReport'))
const EquipmentQuestion = lazy(() => import('@pages/roundsEngine/equipment/EquipmentQuestion'))
import { Route } from 'react-router-dom'
const InspectionReport = lazy(() => import('@analytics/inspection/InspectionReport'))
import * as Sentry from '@sentry/react'
import { handleError } from './utils/globalData'
import { isChunkLoadError, reloadOnceForChunkError } from './utils/chunkReload'
import NewVersionNudge from './components/NewVersionNudge'

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
const history = createBrowserHistory()

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: 'https://3bf71fa3f0690fac5e98a240aea43bfb@o4506359206445056.ingest.sentry.io/4506471185186816',
    ignoreErrors: ['Non-Error promise rejection captured'],
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [],
      } as any),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1, //  Capture 100% of the transactions
    environment: import.meta.env.VITE_APP_TYPE,
    release: import.meta.env.VITE_APP_TYPE,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.5, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })
}
const i18nProvider = polyglotI18nProvider(
  (locale) => {
    if (locale === 'fr') {
      return import('./i18n/fr').then((messages) => messages.default)
    }
    return englishMessages
  },
  'en',
  { allowMissing: true }
)

// const httpClient = (url, options = {}) => {
//   if (!options.headers) {
//     options.headers = new Headers({ Accept: 'application/json' });
//   }
//   // const { token } = JSON.parse(localStorage.getItem('auth'));
//   // options.headers.set('Authorization', `Bearer ${token}`);

//   //options.credentials = 'include';
//   return fetchUtils.fetchJson(url, options);
// }

// const dataProvider = restProvider(
//   `${import.meta.env.VITE_API_HOST}`,
//   httpClient,
//   'X-Total-Count'
// )

// const dataProvider = dataProviderFactory(import.meta.env.VITE_API_HOST)
// const dataProvider = wrapDataProvider(simpleRestProvider(apiBase, httpClient), sseClient);
const Host = import.meta.env.VITE_API_HOST || 'https://v3tjybfbx0.execute-api.us-east-1.amazonaws.com/dev'
const myDataProvider = dataProvider(Host)
export const { store, persistor } = createAdminStore({ authProvider, dataProvider: myDataProvider })

const cacheStore = localStorageStore()

interface LazyRouteEntry {
  path?: string
  index?: boolean
  /** Route turned off for now — not registered at all (see disabledDashboards.ts). */
  disabled?: boolean
  element: ReactElement
  noLayout?: boolean
  permissions?: string[]
  permissionMode?: 'any' | 'all'
  /** Intelligence modules the route needs — any-of. See ProtectedRoute. */
  features?: FeatureName[]
  child?: LazyRouteEntry[]
}

// Routes nest to arbitrary depth (/b/:bId → section → tab → report), and any
// level may declare its own `permissions` guard.
const renderLazyRoute = ({
  path,
  index,
  element,
  child,
  permissions,
  permissionMode,
  features,
  disabled,
}: LazyRouteEntry) => {
  if (disabled) {
    return null
  }
  const guarded =
    permissions || features ? (
      <ProtectedRoute permissions={permissions} mode={permissionMode} features={features}>
        {element}
      </ProtectedRoute>
    ) : (
      element
    )
  const wrapped = <Suspense fallback="">{guarded}</Suspense>
  if (index) {
    return <Route index key="index" element={wrapped} />
  }
  return (
    <Route path={path} key={path} element={wrapped}>
      {child?.map(renderLazyRoute)}
    </Route>
  )
}
const App = () => {
  useEffect(() => {
    // A failed dynamic import (e.g. a non-lazy `import()` for i18n/firebase) after a deploy
    // rejects here. Reload once — guarded so it can't loop — to pick up the new build.
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        console.error('ChunkLoadError (unhandled rejection):', event.reason)
        reloadOnceForChunkError()
      }
    }
    // A `<script>`/asset that fails to load (classic chunk 404). Capture phase to catch it.
    const onResourceError = (event: ErrorEvent) => {
      const target = event.target as HTMLScriptElement
      if (target?.tagName === 'SCRIPT') {
        const src = target.src || ''
        if (src.includes('chunk') || src.includes('/assets/')) {
          handleError('SCRIPT', src)
          reloadOnceForChunkError()
        }
      }
    }
    window.addEventListener('unhandledrejection', onUnhandledRejection)
    window.addEventListener('error', onResourceError, true)
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
      window.removeEventListener('error', onResourceError, true)
    }
  }, [])
  return (
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Provider store={store}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <NewVersionNudge />
              <AppContextProvider>
                <PersistGate loading={null} persistor={persistor}>
                  <Admin
                    dataProvider={myDataProvider}
                    authProvider={authProvider}
                    dashboard={Dashboard}
                    loginPage={Login}
                    layout={Layout}
                    i18nProvider={i18nProvider}
                    disableTelemetry
                    history={history}
                    theme={theme}
                    store={cacheStore}
                    queryClient={queryClient}
                  >
                    <>
                      {customRoutes}
                      {lazyRouter.map((entry) => (
                        <CustomRoutes key={entry.path} noLayout={entry.noLayout}>
                          {renderLazyRoute(entry)}
                        </CustomRoutes>
                      ))}
                      <Resource key="destination" name="destination" />,
                      <Resource name="marketing" key="marketing" list={MarketingList} />,
                      <Resource name="reportMarketing" key="reportMarketing" list={MarketingList} />,
                      <Resource name="operations" key="operations" list={OperationsList} />,
                      <Resource name="tenantExpirenceEvent" key="tenantExpirenceEvent" list={TenantExperienceEvent} />,
                      <Resource name="inspectionReport" key="inspectionReport" list={InspectionReport} />,
                      <Resource
                        name="reportTenantExpirenceEvent"
                        key="reportTenantExpirenceEvent"
                        list={TenantExperienceEvent}
                      />
                      ,
                      <Resource name="propertyReport" key="propertyReport" list={PropertyList} />,
                      <Resource name="reportPropertyReport" key="reportPropertyReport" list={PropertyList} />,
                      <Resource name="subscription" key="subscription" list={SubscriptionList} />,
                      {/* Registered for the dataProvider, but no list route: /subscriptions
                          belongs to the redirect shim that scopes it to the building and
                          applies the AnalyticsSubscriptions guard this route skipped. */}
                      <Resource name="subscriptions" key="subscriptions" />,
                      <Resource name="properties" key="properties" list={AccountPropertyList} create={PropertyAdd} />,
                      <Resource name="importQRCode" key="importQRCode" list={ImportQRCode} />,
                      <Resource name="qrSupportReport" key="qrSupportReport" list={QRSupportReport} />,
                      <Resource name="equipmentQuestion" key="equipmentQuestion" list={EquipmentQuestion} />,
                      {resourceRouter.map(({ path, Element, permissions }) => (
                        <Resource
                          key={path}
                          name={path}
                          list={(props) => (
                            <Suspense fallback="">
                              {permissions ? (
                                <ProtectedRoute permissions={permissions}>
                                  <Element {...props} />
                                </ProtectedRoute>
                              ) : (
                                <Element {...props} />
                              )}
                            </Suspense>
                          )}
                        />
                      ))}
                    </>
                  </Admin>
                </PersistGate>
              </AppContextProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </Provider>
      </LocalizationProvider>
    </QueryClientProvider>
  )
}

export default App
